import { ChevronDown } from 'lucide-react'
import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Timeline, TimelineStep } from '@/components/ui/timeline'
import { listSentence } from '@/lib/formatters'
import { cn } from '@/lib/utils'
import { AcceptTermsStep } from './AcceptTermsStep'
import { ActivationTracker } from './ActivationTracker'
import { CarrierPaymentStep } from './CarrierPaymentStep'
import {
  type CarrierCheckout,
  CheckoutRequirement,
  COVERAGE_LABELS,
  REQUIREMENT_COPY,
  StepStatus,
} from './checkoutData'
import { CoiHoldersStep } from './CoiHoldersStep'
import { CoiStep } from './CoiStep'
import { CoverageActivation } from './CoverageActivation'
import { DocumentUploadStep } from './DocumentUploadStep'
import { MockStripeCardForm } from './MockStripeCardForm'
import { PaymentPlanPicker } from './PaymentPlanPicker'
import { Reveal } from './Reveal'
import { SignFormsStep } from './SignFormsStep'
import { StartDateField } from './StartDateField'
import { StepStatusBadge } from './StepStatusBadge'

function firstOfNextMonth() {
  const today = new Date()
  return new Date(today.getFullYear(), today.getMonth() + 1, 1)
}

const LONG_DATE: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' }

/** One provider's checkout: what's being bought from it, then its requirements in its own order. */
export function CarrierCheckoutCard({
  checkout,
  selectedPlan,
  onSelectPlan,
}: {
  checkout: CarrierCheckout
  selectedPlan?: string
  onSelectPlan: (title: string) => void
}) {
  const { quote, isCarrierEstimate, requirements, paymentPlans } = checkout
  const [completed, setCompleted] = useState<ReadonlySet<CheckoutRequirement>>(new Set())
  const [startDate, setStartDate] = useState<Date | undefined>(firstOfNextMonth)
  const [statuses, setStatuses] = useState<Partial<Record<CheckoutRequirement, StepStatus>>>({})
  const longStartDate = startDate?.toLocaleDateString('en-US', LONG_DATE) ?? ''

  // Undefined follows the current step; null is every step closed.
  const [chosenOpenIndex, setChosenOpenIndex] = useState<number | null>()

  // The first unfinished step in order; anytime steps sit outside the order.
  const currentIndex = requirements.findIndex(
    (requirement) => !REQUIREMENT_COPY[requirement].anytime && !completed.has(requirement)
  )
  // Optional steps don't hold checkout open; once every required step is done, the steps lock.
  const isFinished = requirements.every(
    (requirement) => REQUIREMENT_COPY[requirement].optional || completed.has(requirement)
  )
  const openIndex = isFinished ? null : chosenOpenIndex === undefined ? currentIndex : chosenOpenIndex
  // The step the customer is on is the one they have open; a finished one shows its check instead.
  const activeIndex = openIndex !== null && !completed.has(requirements[openIndex]) ? openIndex : -1

  function toggle(index: number) {
    setChosenOpenIndex(openIndex === index ? null : index)
  }

  // A service is still fetching a document, so the step is waiting on a system, not the customer.
  function isAwaitingDocuments(requirement: CheckoutRequirement) {
    return (
      requirement === CheckoutRequirement.DOCUMENT_UPLOAD &&
      statuses[requirement] !== StepStatus.COMPLETE &&
      (checkout.requiredDocuments ?? []).some((document) => document.awaitingFrom)
    )
  }

  // A carrier estimate opens only the steps that don't depend on final pricing.
  function isLocked(requirement: CheckoutRequirement) {
    return isCarrierEstimate && !REQUIREMENT_COPY[requirement].beforeFinalPricing
  }

  function isAvailable(index: number, done: ReadonlySet<CheckoutRequirement>, current: number) {
    const requirement = requirements[index]
    return (
      !isLocked(requirement) &&
      !done.has(requirement) &&
      (!!REQUIREMENT_COPY[requirement].anytime || index === current)
    )
  }

  // True when some later step is open to the customer, or finishing this one would open one.
  function canAdvanceFrom(index: number, isComplete: boolean) {
    if (isComplete || currentIndex === -1) return true
    return requirements.some((_, later) => later > index && isAvailable(later, completed, currentIndex))
  }

  // Opens the next step the customer can work on, finishing this one first when it's complete.
  function advanceFrom(index: number, isComplete: boolean) {
    const done = isComplete ? new Set(completed).add(requirements[index]) : completed
    const current = requirements.findIndex(
      (requirement) => !REQUIREMENT_COPY[requirement].anytime && !done.has(requirement)
    )
    const target = requirements.findIndex((_, later) => later > index && isAvailable(later, done, current))
    setCompleted(done)
    setChosenOpenIndex(target === -1 ? undefined : target)
  }

  function completeStep(index: number) {
    setCompleted((current) => new Set(current).add(requirements[index]))
    setChosenOpenIndex(undefined)
  }

  function recordStatus(index: number, status: StepStatus) {
    setStatuses((current) => ({ ...current, [requirements[index]]: status }))
  }

  function setStatus(index: number, status: StepStatus) {
    recordStatus(index, status)
    if (status === StepStatus.COMPLETE || status === StepStatus.SUCCESS) completeStep(index)
  }

  // Unlocks the next step as soon as every document is in, but leaves it closed for the customer to open.
  function recordDocumentStatus(index: number, status: StepStatus) {
    recordStatus(index, status)
    if (status !== StepStatus.COMPLETE) return
    setCompleted((current) => new Set(current).add(requirements[index]))
    setChosenOpenIndex((current) => (current === undefined ? openIndex : current))
  }

  return (
    <Card className="overflow-hidden rounded-xl">
      <CardHeader className="flex items-center gap-3">
        <img
          src={quote.iconUrl}
          alt={quote.iconAlt}
          className="border-ink-50 size-9 shrink-0 rounded-full border object-contain"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <CardTitle>{quote.carrierName}</CardTitle>
          <CardDescription>
            {quote.coverages.map((coverage) => COVERAGE_LABELS[coverage.type]).join(' · ')}
          </CardDescription>
        </div>
        {isCarrierEstimate && (
          <Badge variant="outline" className="self-start">
            Carrier estimate
          </Badge>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {/* Done dots and rails in brand-700 with white checks, matching the success badge, instead of accent green. */}
        <Timeline
          className={cn(
            '[&_[data-slot=timeline-step][data-state=done]>div:first-child_svg]:text-white',
            '[&_[data-slot=timeline-step][data-state=done]>div:first-child>div:last-child]:border-brand-700',
            '[&_[data-slot=timeline-step][data-state=done]>div:first-child>div:last-child]:bg-brand-700',
            '[&_[data-slot=timeline-step][data-state=done]_[data-slot=timeline-rail]]:bg-brand-700'
          )}
        >
          {requirements.map((requirement, index) => {
            const copy = REQUIREMENT_COPY[requirement]
            const isDone = completed.has(requirement)
            const isReached =
              !isLocked(requirement) &&
              (isDone || copy.anytime || currentIndex === -1 || index <= currentIndex)
            const isCollapsed = openIndex !== index
            const isClickable = isReached && !isFinished
            // A carrier estimate can't be paid for until the carrier sends final pricing, so payment says why it's shut.
            const isPricingHold = isLocked(requirement) && requirement === CheckoutRequirement.CARRIER_PAYMENT
            const title = (
              // Black whenever it can be opened, even while its dot reads as not started.
              <span
                className={cn(
                  'flex flex-wrap items-center gap-2',
                  isClickable && 'text-foreground font-bold'
                )}
              >
                <span>
                  {typeof copy.title === 'function' ? copy.title(quote.carrierName) : copy.title}
                  {copy.optional && <span className="font-normal"> (optional)</span>}
                </span>
                {/* A done step's checkmark already says it's complete; payment shows its status beside its button. */}
                {!isDone && requirement !== CheckoutRequirement.CARRIER_PAYMENT && (
                  <StepStatusBadge status={statuses[requirement]} />
                )}
                {isPricingHold && <Badge variant="secondary">Requested final quote pricing</Badge>}
              </span>
            )
            return (
              // Only the open step gets the current dot; other open-to-them steps read as not started.
              <TimelineStep
                key={requirement}
                state={
                  isDone
                    ? 'done'
                    : isReached && isAwaitingDocuments(requirement)
                      ? 'processing'
                      : isReached && index === activeIndex
                        ? 'current'
                        : 'pending'
                }
                title={
                  isClickable ? (
                    <span className="flex items-center gap-2">
                      {title}
                      <Button
                        variant="ghost"
                        size="xs"
                        aria-expanded={!isCollapsed}
                        aria-label={isCollapsed ? 'Show details' : 'Hide details'}
                        onClick={() => toggle(index)}
                      >
                        <ChevronDown
                          className={cn('text-ink-300 transition-transform', !isCollapsed && 'rotate-180')}
                        />
                      </Button>
                    </span>
                  ) : (
                    title
                  )
                }
              >
                {/* Mounted from the start so reaching a step animates it open, and collapsing keeps its values. */}
                <Reveal open={isReached && !isCollapsed}>
                  <div className="border-b pb-6">
                    {requirement === CheckoutRequirement.EFFECTIVE_DATE && (
                      <StartDateField
                        id={`${quote.id}-start-date`}
                        date={startDate}
                        onChange={setStartDate}
                        onSave={() => completeStep(index)}
                      />
                    )}
                    {requirement === CheckoutRequirement.SIGNATURES && (
                      <SignFormsStep carrierName={quote.carrierName} onSigned={() => completeStep(index)} />
                    )}
                    {requirement === CheckoutRequirement.PAYMENT_INFO && (
                      <MockStripeCardForm
                        id={`${quote.id}-card`}
                        acceptsAch={checkout.acceptsAch}
                        offerSameCard={checkout.offerSameCard}
                        onSave={() => completeStep(index)}
                      />
                    )}
                    {requirement === CheckoutRequirement.COI && (
                      <CoiStep id={`${quote.id}-coi`} onDone={() => completeStep(index)} />
                    )}
                    {requirement === CheckoutRequirement.COVERAGE_ACTIVATION && (
                      <CoverageActivation
                        coverageNames={listSentence(
                          quote.coverages.map((coverage) => COVERAGE_LABELS[coverage.type])
                        )}
                        startDate={longStartDate}
                      />
                    )}
                    {requirement === CheckoutRequirement.DOCUMENT_UPLOAD && (
                      <DocumentUploadStep
                        carrierName={quote.carrierName}
                        documents={checkout.requiredDocuments ?? []}
                        onStatusChange={(status) => recordDocumentStatus(index, status)}
                        canAdvance={(isComplete) => canAdvanceFrom(index, isComplete)}
                        onNext={(isComplete) => advanceFrom(index, isComplete)}
                      />
                    )}
                    {requirement === CheckoutRequirement.TERMS && (
                      <AcceptTermsStep
                        id={`${quote.id}-terms`}
                        onAccept={() => setStatus(index, StepStatus.COMPLETE)}
                      />
                    )}
                    {requirement === CheckoutRequirement.COI_HOLDERS && (
                      <CoiHoldersStep
                        id={`${quote.id}-coi-holders`}
                        canAdvance={canAdvanceFrom(index, false)}
                        onDone={() => advanceFrom(index, true)}
                      />
                    )}
                    {requirement === CheckoutRequirement.CARRIER_PAYMENT && (
                      <CarrierPaymentStep
                        carrierName={quote.carrierName}
                        startDate={longStartDate}
                        status={statuses[requirement]}
                        onStatusChange={(status) => setStatus(index, status)}
                      />
                    )}
                    {requirement === CheckoutRequirement.PAYMENT_PLAN && (
                      <PaymentPlanPicker
                        plans={paymentPlans}
                        selected={selectedPlan}
                        onSelect={(title) => {
                          onSelectPlan(title)
                          completeStep(index)
                        }}
                      />
                    )}
                  </div>
                </Reveal>
              </TimelineStep>
            )
          })}
        </Timeline>
        {checkout.tracksActivation && (
          <ActivationTracker
            carrierName={quote.carrierName}
            isPaid={statuses[CheckoutRequirement.CARRIER_PAYMENT] === StepStatus.SUCCESS}
            startDate={startDate}
            activatesInSeconds={checkout.activatesInSeconds}
          />
        )}
      </CardContent>
    </Card>
  )
}
